import { api } from './api';

/**
 * Feature Flag Service
 * 
 * Provides frontend access to feature flags with caching and reactive updates.
 * Follows the same pattern as billingService.
 */
export const featureFlagService = {
    // In-memory cache of flags
    _flags: {},
    _lastFetch: null,
    _cacheTTL: 5 * 60 * 1000, // 5 minutes (matches backend cache)
    
    /**
     * Get all active feature flags for current user
     * Results are cached for 5 minutes
     *
     * @param {boolean} forceRefresh - Force refresh even if cache is valid
     * @param {object} [options]
     * @param {boolean} [options.skipLoading] - Suppress the global page spinner
     * @returns {Promise<{flags: Object, evaluatedAt: string}>}
     */
    async getActiveFlags(forceRefresh = false, options = {}) {
        const now = Date.now();

        // Return cached flags if still valid
        if (!forceRefresh && this._lastFetch && (now - this._lastFetch < this._cacheTTL)) {
            console.log('[FeatureFlagService] Returning cached flags');
            return {
                flags: this._flags,
                evaluatedAt: new Date(this._lastFetch).toISOString(),
                fromCache: true
            };
        }

        console.log('[FeatureFlagService] Fetching active flags from API');
        const response = await api.get('/feature-flags/active', { skipLoading: !!options.skipLoading });
        
        // Update cache
        this._flags = response.data.flags || {};
        this._lastFetch = now;
        
        console.log('[FeatureFlagService] Flags loaded:', Object.keys(this._flags).length);
        
        return {
            flags: this._flags,
            evaluatedAt: response.data.evaluatedAt,
            fromCache: false
        };
    },
    
    /**
     * Check if a specific feature is enabled
     * Uses cached flags if available, otherwise fetches
     *
     * @param {string} flagKey - The feature flag key to check
     * @param {object} [options]
     * @param {boolean} [options.skipLoading] - Suppress the global page spinner
     *   on the fallback fetch (cache hit never hits the network).
     * @returns {Promise<boolean>}
     */
    async isEnabled(flagKey, options = {}) {
        // Try to use cache first
        if (this._lastFetch && (Date.now() - this._lastFetch < this._cacheTTL)) {
            const enabled = this._flags[flagKey] === true;
            console.log(`[FeatureFlagService] ${flagKey}: ${enabled} (cached)`);
            return enabled;
        }

        // Cache expired or not loaded, fetch fresh
        try {
            const response = await this.getActiveFlags(false, options);
            const enabled = response.flags[flagKey] === true;
            console.log(`[FeatureFlagService] ${flagKey}: ${enabled} (fetched)`);
            return enabled;
        } catch (error) {
            console.error(`[FeatureFlagService] Error checking flag ${flagKey}:`, error);
            // Fail open - allow feature if check fails
            return true;
        }
    },
    
    /**
     * Check detailed status of a specific flag (for debugging)
     * 
     * @param {string} flagKey - The feature flag key to check
     * @returns {Promise<{enabled: boolean, reason: string, source: string}>}
     */
    async checkFlagStatus(flagKey) {
        console.log(`[FeatureFlagService] Checking detailed status for ${flagKey}`);
        const response = await api.get(`/feature-flags/${flagKey}/status`);
        return response.data;
    },
    
    /**
     * Synchronous check using cached flags only
     * Returns false if flags not loaded yet
     * 
     * @param {string} flagKey - The feature flag key to check
     * @returns {boolean}
     */
    isEnabledSync(flagKey) {
        if (!this._lastFetch) {
            console.warn(`[FeatureFlagService] Flags not loaded yet, returning false for ${flagKey}`);
            return false;
        }
        
        return this._flags[flagKey] === true;
    },
    
    /**
     * Get all cached flags (synchronous)
     * 
     * @returns {Object} Map of flag keys to boolean values
     */
    getAllSync() {
        return { ...this._flags };
    },
    
    /**
     * Clear the cache (useful for testing or force refresh)
     */
    clearCache() {
        console.log('[FeatureFlagService] Clearing cache');
        this._flags = {};
        this._lastFetch = null;
    },
    
    /**
     * Initialize flags on app startup
     * Should be called once during app initialization
     * 
     * @returns {Promise<Object>} The loaded flags
     */
    async initialize() {
        console.log('[FeatureFlagService] Initializing feature flags');
        try {
            const response = await this.getActiveFlags(true, { skipLoading: true });
            console.log('[FeatureFlagService] Initialization complete');
            return response.flags;
        } catch (error) {
            console.error('[FeatureFlagService] Failed to initialize flags:', error);
            // Return empty object on error - don't block app startup
            return {};
        }
    },
    
    /**
     * Get all active feature flags - alias for getActiveFlags for backward compatibility
     * 
     * @param {boolean} forceRefresh - Force refresh even if cache is valid
     * @returns {Promise<Object>} Map of flag keys to boolean values
     */
    async getFlags(forceRefresh = false) {
        const response = await this.getActiveFlags(forceRefresh);
        return response.flags;
    }
};
